// apps/spa/src/App.tsx
// Real provider tree + router (replaces the 03-00 placeholder).
// Tree: QueryClientProvider > BrowserRouter > Routes + SessionExpiredModal + Toaster.
//
// Routes:
//   /login                    -> <Login /> (unprotected)
//   /auth/role-required       -> <RoleRequired /> (unprotected)
//   * (everything else)       -> wrapped in <RouteGuard /> which renders <AppShell />
//        index                -> redirect to /reminders
//        /reminders           -> <RemindersList />
//        /reminders/new       -> <ReminderNew />
//        /reminders/:id/edit  -> <ReminderEdit />
//        /commands            -> <CommandsList />          [Phase 4]
//        /commands/new        -> <CommandNew />             [Phase 4]
//        /commands/:id/edit   -> <CommandEdit />            [Phase 4]
//        /audit               -> <AuditLog /> (placeholder; 04-03 fills in full viewer)
//        /settings/roles      -> <RoleToggleManager /> [Phase 7]
//
// SessionExpiredModal is a sibling of <Routes> so it can render over any route.
//
// Phase 7 (Plan 07-05): AnimatedRoutes provides 0.15s opacity crossfade between top-level routes.

import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { Toaster } from '@/components/ui/sonner';
import { queryClient } from '@/lib/queryClient';
import { RouteGuard } from '@/components/auth/RouteGuard';
import { SessionExpiredModal } from '@/components/auth/SessionExpiredModal';
import { Login } from '@/routes/Login';
import { RoleRequired } from '@/routes/RoleRequired';
import { RemindersList } from '@/routes/RemindersList';
import { ReminderNew } from '@/routes/ReminderNew';
import { ReminderEdit } from '@/routes/ReminderEdit';
import { CommandsList } from '@/routes/CommandsList';
import { CommandNew } from '@/routes/CommandNew';
import { CommandEdit } from '@/routes/CommandEdit';
import AuditLog from '@/routes/AuditLog';
import { RoleToggleManager } from '@/components/settings/RoleToggleManager';

// ---------------------------------------------------------------------------
// Route transition wrapper — 150ms opacity crossfade between top-level routes.
// Uses useLocation so each pathname change triggers a unique key → AnimatePresence
// sees it as a new child and plays enter/exit.
// ---------------------------------------------------------------------------

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{ width: '100%' }}
      >
        <Routes location={location}>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/role-required" element={<RoleRequired />} />
          <Route element={<RouteGuard />}>
            <Route index element={<Navigate to="/reminders" replace />} />
            {/* Reminders (Phase 3) */}
            <Route path="/reminders" element={<RemindersList />} />
            <Route path="/reminders/new" element={<ReminderNew />} />
            <Route path="/reminders/:id/edit" element={<ReminderEdit />} />
            {/* Commands (Phase 4 — Plan 04-01) */}
            <Route path="/commands" element={<CommandsList />} />
            <Route path="/commands/new" element={<CommandNew />} />
            <Route path="/commands/:id/edit" element={<CommandEdit />} />
            {/* Audit log (Phase 4 — Plan 04-03) */}
            <Route path="/audit" element={<AuditLog />} />
            {/* Settings (Phase 7 — 07-03): self-service role list for /role-toggle */}
            <Route path="/settings/roles" element={<RoleToggleManager />} />
          </Route>
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AnimatedRoutes />
        <SessionExpiredModal />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
